import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';

import { pageContext, get } from 'ctm-util-functions';

import { FieldSet } from '../field_set/field_set';
import { SimpleFlexbox } from '../../../flexbox/flexbox';
import { BackButton } from '../../../back_button/back_button';
import { FadeIn } from '../../../animations/fadeIn';

class Form extends Component {

    constructor(props) {
        super(props);

        this.nextStepButtonVisualProps = {
            UIComponent: 'btn_secondary_xlarge_semi_rounded',
            variant: 'full_width'
        };
        this.formSchemaModel = pageContext.getProps().models.formSchemaModel;
        this.formElementFactory = pageContext.getProps().factories.formElement;
        this.formProperties = pageContext.getProps().models.formSchemaModel.properties;
    }

    /**
     * Returns the back link if its required
     * @returns {null|node} The back button or nothing!
     */
    getBackButton = () => {
        const pageName = pageContext.props.pageState.pageName;
        const isFirstPage = pageContext.props.models.pageSchemaModel.isFirstStepInJourney(pageName);
        const showBackButton = get(this.formProperties['vertical.quote'], 'showBackButton', true);
        const contentProps = { label: 'Back', iconPosition: 'before', icon: 'ctm_angle_left' };
        const elementProps = { tagName: 'a', href: 'javascript:;' };
        return (!isFirstPage && showBackButton && (
            <SimpleFlexbox display="flex" xs={12} xsOrder={2} sm={2} lg={1} itemsCenter="sm" contentCenter="xs" contentStart="sm" margin="top_bottom">
                <BackButton
                    contentProps={contentProps}
                    elementProps={elementProps} />
            </SimpleFlexbox>
        )) || null;
    };

    /**
     * Use the form element factory to render the next step button
     * @param {Object} schema The nextstepbutton schema from quote form schema
     * @param {String} pageName The current form's 'name' property.
     * @returns {Node} A NextStepButton component
     */
    getNextStepButton = (schema, pageName) => {
        return this.formElementFactory.createNextStepButton({
            visualProps: this.nextStepButtonVisualProps,
            ...schema,
            pageName
        });
    };

    /**
     * Create and return the NextStepButton and back button for this form
     * @returns {Node} - The NextStepButton for this form
     */
    getNavigationControls = () => {
        const { schema } = this.props;
        if (get(this.props, 'schema.nextStepButton') === undefined) {
            return null;
        }
        const centerNextStepButton = get(
            this.formProperties['vertical.quote'],
            'centerNextStepButton',
            false
        );
        const { nextStepButton, name } = schema;
        const containerProps = centerNextStepButton ? { display: 'block', contentCenter: 'xs' } : { display: 'flex' };
        const buttonProps = centerNextStepButton ? { margin: 'auto' } : {};
        return (
            <SimpleFlexbox wrap {...containerProps}>
                {this.getBackButton()}
                <SimpleFlexbox xs={12} xsOrder={1} sm={7} {...buttonProps}>
                    {this.getNextStepButton(nextStepButton, name)}
                </SimpleFlexbox>
            </SimpleFlexbox>
        );
    };

    /**
     * Create the FieldSets for this form based on the elements in this.props.schema
     * @returns {Array} - The array of FieldSets
     */
    getFieldSets = () => {
        const { breakpoint, extraProps } = this.props;
        return get(this.props, 'schema.elements', []).map(
            (schema) => {
                let fieldSetComponent = FieldSet;
                if (schema.fieldSetType && schema.fieldSetType !== 'fieldSet') {
                    const customFieldSets = get(pageContext.getProps().verticalComponents, 'CustomFieldSets', {});
                    fieldSetComponent = customFieldSets[schema.fieldSetType];
                }
                if (!fieldSetComponent) {
                    throw Error(`A FieldSet with elType: ${schema.elType} was defined, but this elType doesn't exist`);
                }
                return React.createElement(
                    fieldSetComponent,
                    {
                        key: `${schema.identifier}_fieldSet`,
                        schema,
                        breakpoint,
                        extraProps
                    }
                );
            }
        );
    };

    /**
     * Prevent the form from being submitted by pressing enter.
     * @param {Event} event The form event handler
     */
    onSubmit = (event) => {
        event.preventDefault();
    };

    /**
     * Render the form and next step button
     * @returns {Node} - The form component
     */
    render = () => {
        let component = (
            <>
                <form noValidate autoComplete="off" onSubmit={this.onSubmit}>{this.getFieldSets()}</form>
                {this.getNavigationControls()}
            </>
        );
        if (get(this.props, 'schema.fadeIn', true)) {
            component = <FadeIn>{component}</FadeIn>;
        }
        return component;
    };

}

Form.propTypes = {
    schema: PropTypes.shape({
        name: PropTypes.string,
        nextStepButton: PropTypes.object,
        fadeIn: PropTypes.bool,
        elements: PropTypes.array,
        showBackButton: PropTypes.bool
    }).isRequired,
    breakpoint: PropTypes.string,
    extraProps: PropTypes.object
};

export { Form };
